// assets/custom-design-upload.js
import { Component } from '@theme/component';

/**
 * @typedef {object} CustomDesignUploadRefs
 * @property {HTMLInputElement} fileInput - The file input joined to the product form.
 * @property {HTMLElement} fileName - Shows the chosen file name.
 * @property {HTMLElement} error - Shows validation errors.
 */

/**
 * Validates the custom design file before Horizon's product form submits it.
 * The product form posts FormData, so the file travels as a multipart line item property.
 * @extends Component<CustomDesignUploadRefs>
 */
class CustomDesignUpload extends Component {
  requiredRefs = ['fileInput', 'fileName', 'error'];

  /** @type {string} */
  #emptyLabel = '';

  connectedCallback() {
    super.connectedCallback();
    this.#emptyLabel = this.refs.fileName.textContent ?? '';
  }

  handleChange() {
    const { fileInput, fileName } = this.refs;
    const file = fileInput.files?.[0];

    if (!file) {
      fileName.textContent = this.#emptyLabel;
      this.#setError('');
      return;
    }

    fileName.textContent = file.name;

    const maxBytes = Number(this.dataset.maxBytes) || 0;
    if (maxBytes && file.size > maxBytes) {
      this.#setError(this.dataset.errorSize ?? '');
      return;
    }

    if (!this.#isAccepted(file)) {
      this.#setError(this.dataset.errorType ?? '');
      return;
    }

    this.#setError('');
  }

  /**
   * @param {File} file
   * @returns {boolean}
   */
  #isAccepted(file) {
    const accept = this.refs.fileInput.accept;
    if (!accept) return true;

    const name = file.name.toLowerCase();
    const type = file.type.toLowerCase();

    return accept
      .split(',')
      .map((rule) => rule.trim().toLowerCase())
      .filter(Boolean)
      .some((rule) => {
        if (rule.startsWith('.')) return name.endsWith(rule);
        if (rule.endsWith('/*')) return type.startsWith(rule.slice(0, -1));
        return type === rule;
      });
  }

  /**
   * Uses the native validity API so the product form's checkValidity() blocks add to cart.
   * @param {string} message
   */
  #setError(message) {
    const { fileInput, error } = this.refs;
    fileInput.setCustomValidity(message);
    error.textContent = message;
    error.classList.toggle('hidden', !message);
  }
}

if (!customElements.get('custom-design-upload-component')) {
  customElements.define('custom-design-upload-component', CustomDesignUpload);
}
